<script setup lang="ts">
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { IconImage, SimpleLoading } from '@/shared/ui';
  import { useCustomToast } from '@/shared/lib';
  import ArrowDown from '@/shared/assets/icons/ArrowDown.svg';
  import BranchesIcon from '@/shared/assets/icons/building.svg';
  import { useAuthStore } from '@/entities/Session/@x/Branch';
  import { branchApi, setCurrentSelectedBranch, type Branch } from '@/entities/Branch';

  const SIDEBAR_TRANSITION_DELAY_MS = 300;

  interface Props {
    label?: string;
    icon?: string;
    disabled?: boolean;
    containerClass?: string;
    buttonClass?: string;
    arrowRight?: string;
    isCollapsed?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    label: 'Filial',
    arrowRight: 'right-4',
    isCollapsed: false,
  });

  const emit = defineEmits<{
    expand: [];
  }>();

  const router = useRouter();
  const authStore = useAuthStore();
  const toast = useCustomToast();

  const branches = ref<Branch[]>([]);
  const currentBranchId = ref<number | undefined>();
  const selectedBranch = ref('');
  const isOpen = ref(false);
  const isSwitching = ref(false);
  const shouldOpenAfterExpand = ref(false);
  const dropdownRef = ref<HTMLDivElement>();

  const branchOptions = computed(() =>
    branches.value.map((branch) => ({
      id: branch.id,
      name: branch.name,
      disabled: branch.id === currentBranchId.value,
    }))
  );

  const selectedOption = computed(() => {
    return branchOptions.value.find((option) => String(option.id) === String(selectedBranch.value));
  });

  const isDisabled = computed(() => props.disabled || isSwitching.value);

  const loadBranches = async () => {
    try {
      const data = await branchApi.getUserBranches();
      branches.value = data.branches;
      currentBranchId.value = data.currentBranchId;
      if (data.currentBranchId !== undefined) {
        selectedBranch.value = String(data.currentBranchId);
        setCurrentSelectedBranch(String(data.currentBranchId));
      }
    } catch {
      // Branch loading failed silently - user will see empty branch list
    }
  };

  const toggleDropdown = () => {
    if (isDisabled.value) return;

    if (props.isCollapsed) {
      shouldOpenAfterExpand.value = true;
      void loadBranches();
      emit('expand');
      return;
    }

    if (!isOpen.value) {
      void loadBranches();
    }
    isOpen.value = !isOpen.value;
  };

  const selectOption = async (option: {
    id: string | number;
    name: string;
    disabled?: boolean;
  }) => {
    if (option.disabled || isSwitching.value) return;

    const newBranchId = Number(option.id);
    if (newBranchId === currentBranchId.value) return;

    const previousBranchId = currentBranchId.value;
    const previousSelectedBranch = selectedBranch.value;

    selectedBranch.value = String(option.id);
    isOpen.value = false;
    shouldOpenAfterExpand.value = false;
    isSwitching.value = true;

    try {
      await authStore.switchBranch(newBranchId);
      currentBranchId.value = newBranchId;

      toast.showSuccessToast({
        title: 'Filial alterada',
        message: `Você está agora na filial ${option.name}`,
        type: 'success',
      });

      router.push({ path: '/', force: true });
    } catch {
      currentBranchId.value = previousBranchId;
      selectedBranch.value = previousSelectedBranch;

      toast.showErrorToast({
        title: 'Erro ao trocar filial',
        message: 'Não foi possível trocar de filial. Tente novamente.',
        type: 'error',
      });
    } finally {
      isSwitching.value = false;
    }
  };

  const resetToCurrentBranch = async () => {
    if (currentBranchId.value !== undefined) {
      selectedBranch.value = String(currentBranchId.value);
    }
  };

  const handleClickOutside = (e: Event) => {
    if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
      isOpen.value = false;
      shouldOpenAfterExpand.value = false;
    }
  };

  const openDropdownAfterExpand = async () => {
    await nextTick();
    setTimeout(() => {
      isOpen.value = true;
      shouldOpenAfterExpand.value = false;
    }, SIDEBAR_TRANSITION_DELAY_MS);
  };

  defineExpose({
    resetToCurrentBranch,
    openDropdownAfterExpand,
  });

  watch(
    () => authStore.isAuthenticated,
    async (isAuthenticated) => {
      if (isAuthenticated) {
        await loadBranches();
      }
    },
    { immediate: true }
  );

  watch(
    () => props.isCollapsed,
    async (newValue, oldValue) => {
      if (oldValue === true && newValue === false && shouldOpenAfterExpand.value) {
        await openDropdownAfterExpand();
      }
    }
  );

  onMounted(async () => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<template>
  <!-- Modo colapsado: apenas ícone -->
  <div
    v-if="isCollapsed"
    ref="dropdownRef"
    class="relative"
  >
    <button
      type="button"
      class="w-10 h-10 flex items-center justify-center rounded-lg bg-logout focus:outline-none"
      :disabled="isDisabled"
      @click="toggleDropdown"
    >
      <SimpleLoading
        v-if="isSwitching"
        :loading-dimensions="{ width: 'w-5', height: 'h-5' }"
      />
      <IconImage
        v-else
        :src="BranchesIcon"
        alt="Seta dropdown"
        class="w-6 h-6 text-[var(--theme-secondary-ink)]"
      />
    </button>
  </div>

  <!-- Modo expandido: componente completo -->
  <div
    v-else
    ref="dropdownRef"
    class="w-50 relative mt-2"
  >
    <IconImage
      v-if="icon"
      :src="icon"
      :alt="icon"
      class="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10"
    />

    <button
      type="button"
      :class="[
        'w-full px-4 py-2.5 bg-logout rounded-lg text-[var(--theme-text)] text-sm/5.5 focus:outline-none pr-12 z-10 text-left flex items-center gap-2',
        icon ? 'pl-10' : 'pl-4',
        isOpen && props.buttonClass?.includes('border') ? 'focus:border-gray-07' : '',
        isSwitching ? 'opacity-70 cursor-wait' : '',
      ]"
      :disabled="isDisabled"
      @click="toggleDropdown"
    >
      <SimpleLoading
        v-if="isSwitching"
        :loading-dimensions="{ width: 'w-5', height: 'h-5' }"
      />
      <IconImage
        v-else
        :src="BranchesIcon"
        alt="Seta dropdown"
        class="w-6 h-6"
      />
      <span
        v-if="label && !selectedOption"
        class="text-[var(--theme-text)]"
      >
        {{ label }}
      </span>
      <span v-else-if="selectedOption">
        {{ selectedOption.name }}
      </span>
    </button>

    <IconImage
      :src="ArrowDown"
      alt="Seta dropdown"
      :class="[
        'absolute top-1/2 transform -translate-y-1/2 pointer-events-none w-4 h-4 text-[var(--theme-secondary-ink)]',
        arrowRight,
      ]"
    />

    <div
      v-if="isOpen && !isDisabled"
      class="absolute top-full left-0 right-0 mt-1 p-3 bg-logout rounded-2xl z-20 max-h-60 overflow-y-auto"
    >
      <div
        v-for="option in branchOptions"
        :key="option.id"
        :class="[
          'px-3 py-2 cursor-pointer text-sm rounded-md transition-colors duration-150',
          String(option.id) === String(selectedBranch)
            ? 'bg-selected-secondary font-medium'
            : 'text-[var(--theme-text)]',
          option.disabled ? 'cursor-not-allowed' : '',
        ]"
        @click="selectOption(option)"
      >
        {{ option.name }}
      </div>
    </div>
  </div>
</template>

<style scoped>
  .bg-logout {
    background-color: color-mix(in srgb, var(--theme-primary) 95%, var(--theme-secondary));
  }

  .bg-selected-secondary {
    background-color: color-mix(in srgb, var(--theme-secondary) 30%, var(--theme-primary));
    color: var(--theme-text);
  }
</style>
